<template>
  <main class="home">
    <div class="hero-grid">
      <section class="hero">
        <p class="eyebrow">// développeur</p>
        <h1 ref="heroTitle" class="hero-title" aria-label="Alexis Mayeur">
          <span v-for="(ch, i) in titleChars" :key="i" class="char" aria-hidden="true">{{ ch === ' ' ? '\u00A0' : ch }}</span>
        </h1>
        <p class="tagline">
          Je conçois et développe des projets logiciels, du prototype
          expérimental à l'application complète.
        </p>
        <div class="cta-row">
          <router-link
            ref="ctaBtn"
            to="/projets"
            class="btn btn-primary"
            @mousemove="handleBtnMove"
            @mouseleave="resetBtn"
          >
            <span ref="ctaLabel" class="btn-label">Voir mes projets</span>
          </router-link>
        </div>
      </section>

      <aside class="hero-media" v-if="featured && featured.screenshots.length">
        <a
          ref="preview"
          class="preview"
          :href="featured.url"
          target="_blank"
          rel="noopener"
          @mousemove="handlePreviewMove"
          @mouseleave="resetPreviewTilt"
        >
          <transition name="fade" mode="out-in">
            <img
              :key="activeIndex"
              :src="featured.screenshots[activeIndex]"
              :alt="`Capture d'écran ${featured.name}`"
            >
          </transition>
          <div class="preview-overlay">
            <p class="preview-title">
              {{ featured.name }}
              <span v-if="version" class="preview-version">v{{ version }}</span>
            </p>
            <p class="preview-description">{{ featured.description }}</p>
            <span class="preview-cta">Visiter le site ↗</span>
          </div>
        </a>
        <div class="thumbs">
          <button
            v-for="(shot, i) in featured.screenshots"
            :key="shot"
            type="button"
            class="thumb"
            :class="{ active: i === activeIndex }"
            @click="activeIndex = i"
          >
            <img :src="shot" :alt="`Miniature ${i + 1}`">
          </button>
        </div>
      </aside>
    </div>

    <section ref="liveSection" class="live-section">
      <p class="eyebrow">// live</p>
      <h2>En direct sur Twitch</h2>
      <div class="twitch-embed">
        <iframe
          class="twitch-player"
          :src="twitchPlayerUrl"
          title="Lecteur Twitch VaticUI"
          allow="autoplay; fullscreen"
          allowfullscreen
        />
        <iframe
          class="twitch-chat"
          :src="twitchChatUrl"
          title="Chat Twitch VaticUI"
        />
      </div>
    </section>
  </main>
</template>

<script>
import gsap from 'gsap'
import { getProjects } from '@/services/api'
import { prefersReducedMotion } from '@/composables/useReducedMotion'

export default {
  name: 'HomeView',
  data () {
    return {
      activeIndex: 0,
      version: null,
      featured: null,
      titleChars: 'Alexis Mayeur'.split(''),
      reducedMotion: false,
      twitchChannel: 'vaticui'
    }
  },
  computed: {
    twitchParent () {
      return window.location.hostname || 'alexismayeur.fr'
    },
    twitchPlayerUrl () {
      return `https://player.twitch.tv/?channel=${this.twitchChannel}&parent=${this.twitchParent}&muted=true`
    },
    twitchChatUrl () {
      return `https://www.twitch.tv/embed/${this.twitchChannel}/chat?parent=${this.twitchParent}&darkpopout`
    }
  },
  async created () {
    try {
      const projects = await getProjects()
      this.featured = projects.find(p => p.featured) || projects[0] || null
      if (this.featured && this.featured.versionUrl) {
        this.fetchVersion(this.featured.versionUrl)
      }
    } catch (e) {
      // pas de projet mis en avant si l'API est injoignable
    }
  },
  mounted () {
    this.reducedMotion = prefersReducedMotion()
    if (this.reducedMotion) {
      if (this.$refs.liveSection) gsap.set(this.$refs.liveSection, { opacity: 1 })
      return
    }

    if (this.$refs.liveSection) {
      gsap.fromTo(this.$refs.liveSection,
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 0.7,
          ease: 'power3.out',
          scrollTrigger: { trigger: this.$refs.liveSection, start: 'top 88%' }
        }
      )
    }

    gsap.set(this.$refs.heroTitle, { perspective: 600 })
    gsap.from(this.$refs.heroTitle.querySelectorAll('.char'), {
      yPercent: 120,
      opacity: 0,
      rotateX: -50,
      duration: 0.55,
      stagger: 0.025,
      ease: 'back.out(1.7)',
      delay: 0.15
    })

    this.magX = gsap.quickTo(this.$refs.ctaBtn.$el, 'x', { duration: 0.4, ease: 'power3.out' })
    this.magY = gsap.quickTo(this.$refs.ctaBtn.$el, 'y', { duration: 0.4, ease: 'power3.out' })
    this.labelX = gsap.quickTo(this.$refs.ctaLabel, 'x', { duration: 0.4, ease: 'power3.out' })
    this.labelY = gsap.quickTo(this.$refs.ctaLabel, 'y', { duration: 0.4, ease: 'power3.out' })
  },
  watch: {
    featured (val) {
      if (this.reducedMotion || !val) return
      this.$nextTick(() => {
        if (!this.$refs.preview) return
        gsap.fromTo(this.$refs.preview,
          { opacity: 0, scale: 0.94, filter: 'brightness(0.6)' },
          { opacity: 1, scale: 1, filter: 'brightness(1)', duration: 0.7, ease: 'power3.out' }
        )
        gsap.set(this.$refs.preview, { transformPerspective: 800 })
        this.tiltX = gsap.quickTo(this.$refs.preview, 'rotateX', { duration: 0.5, ease: 'power3.out' })
        this.tiltY = gsap.quickTo(this.$refs.preview, 'rotateY', { duration: 0.5, ease: 'power3.out' })
      })
    }
  },
  methods: {
    async fetchVersion (versionUrl) {
      try {
        const res = await fetch(versionUrl)
        const text = await res.text()
        const match = text.match(/^version:\s*(.+)$/m)
        if (match) this.version = match[1].trim()
      } catch (e) {
        // version indisponible, on affiche l'aperçu sans badge
      }
    },
    handlePreviewMove (e) {
      if (this.reducedMotion || !this.tiltX) return
      const rect = e.currentTarget.getBoundingClientRect()
      const px = (e.clientX - rect.left) / rect.width - 0.5
      const py = (e.clientY - rect.top) / rect.height - 0.5
      this.tiltX(py * -8)
      this.tiltY(px * 8)
    },
    resetPreviewTilt () {
      if (!this.tiltX) return
      this.tiltX(0)
      this.tiltY(0)
    },
    handleBtnMove (e) {
      if (this.reducedMotion || !this.magX) return
      const rect = e.currentTarget.getBoundingClientRect()
      const x = (e.clientX - rect.left - rect.width / 2)
      const y = (e.clientY - rect.top - rect.height / 2)
      this.magX(x * 0.35)
      this.magY(y * 0.5)
      this.labelX(x * 0.15)
      this.labelY(y * 0.2)
    },
    resetBtn () {
      if (!this.magX) return
      this.magX(0)
      this.magY(0)
      this.labelX(0)
      this.labelY(0)
    }
  }
}
</script>

<style scoped>
.home {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12vh;
  padding: 8vh 6vw 12vh;
}

.hero-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 48px;
  align-items: center;
  width: 100%;
  max-width: 1400px;
  min-height: 60vh;
  margin: 0 auto;
}

@media (min-width: 900px) {
  .hero-grid {
    grid-template-columns: minmax(0, 1fr) minmax(360px, 620px);
  }
}

.hero {
  max-width: 640px;
  text-align: left;
}

.eyebrow {
  font-family: 'Consolas', 'Courier New', monospace;
  color: var(--accent);
  margin: 0 0 8px;
  font-size: 0.9rem;
  opacity: 0;
  animation: fade-in-up 0.5s ease 0.05s both;
}

.eyebrow::after {
  content: '';
  display: inline-block;
  width: 8px;
  height: 1em;
  margin-left: 4px;
  background: var(--accent);
  vertical-align: text-bottom;
  animation: blink 1s step-end infinite;
}

.hero-title {
  font-size: clamp(2.4rem, 6vw, 4rem);
  margin: 0 0 16px;
  overflow: hidden;
}

.hero-title .char {
  display: inline-block;
  will-change: transform, opacity;
}

.tagline {
  color: var(--text-dim);
  font-size: 1.1rem;
  line-height: 1.6;
  margin: 0 0 32px;
  opacity: 0;
  animation: fade-in-up 0.5s ease 0.55s both;
}

.cta-row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  opacity: 0;
  animation: fade-in-up 0.5s ease 0.65s both;
}

.btn {
  display: inline-block;
  padding: 12px 24px;
  border-radius: 6px;
  text-decoration: none;
  font-weight: 600;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
  will-change: transform;
}

.btn-label {
  display: inline-block;
  will-change: transform;
}

.btn-primary {
  background: var(--accent);
  color: #0d1117;
}

.btn-primary:hover {
  box-shadow: 0 8px 20px -8px rgba(63, 185, 80, 0.6);
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

.hero-media {
  opacity: 0;
  animation: fade-in-up 0.6s ease 0.3s both;
}

.preview {
  position: relative;
  display: block;
  border: 1px solid var(--border);
  border-radius: 12px;
  overflow: hidden;
  background: #000;
  aspect-ratio: 16 / 10;
  margin-bottom: 12px;
  box-shadow: 0 24px 48px -24px rgba(0, 0, 0, 0.6);
  cursor: pointer;
  will-change: transform, filter;
  transform-style: preserve-3d;
}

.preview img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.preview-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px;
  text-align: center;
  background: rgba(13, 17, 23, 0.9);
  opacity: 0;
  transform: translateY(6px);
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.preview:hover .preview-overlay,
.preview:focus-visible .preview-overlay {
  opacity: 1;
  transform: translateY(0);
}

.preview-title {
  margin: 0;
  display: flex;
  align-items: baseline;
  gap: 10px;
  font-family: 'Consolas', 'Courier New', monospace;
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--text);
}

.preview-version {
  font-size: 0.85rem;
  font-weight: 400;
  color: var(--text-dim);
}

.preview-description {
  margin: 0;
  max-width: 380px;
  color: var(--text-dim);
  line-height: 1.5;
}

.preview-cta {
  margin-top: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--accent);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.thumbs {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.thumb {
  width: 88px;
  height: 55px;
  padding: 0;
  border-radius: 6px;
  overflow: hidden;
  border: 2px solid transparent;
  cursor: pointer;
  opacity: 0.6;
  background: none;
  transition: opacity 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
}

.thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.thumb:hover {
  opacity: 1;
  transform: translateY(-2px);
}

.thumb.active {
  opacity: 1;
  border-color: var(--accent);
}

@media (prefers-reduced-motion: reduce) {
  .eyebrow, .tagline, .cta-row, .hero-media {
    animation: none;
    opacity: 1;
  }
}

.live-section {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  text-align: left;
}

.live-section h2 {
  margin: 0 0 24px;
  font-size: clamp(1.6rem, 4vw, 2.4rem);
}

.twitch-embed {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  border: 1px solid var(--border);
  border-radius: 12px;
  overflow: hidden;
  background: var(--bg-alt);
  box-shadow: 0 24px 48px -28px rgba(0, 0, 0, 0.6);
}

.twitch-player,
.twitch-chat {
  width: 100%;
  height: 480px;
  border: none;
  display: block;
}

.twitch-chat {
  border-left: 1px solid var(--border);
}

@media (max-width: 800px) {
  .twitch-embed {
    grid-template-columns: 1fr;
  }

  .twitch-player {
    height: 56.25vw;
    max-height: 400px;
  }

  .twitch-chat {
    height: 380px;
    border-left: none;
    border-top: 1px solid var(--border);
  }
}
</style>
